import { Alert, Box, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import React, { useCallback, type ReactNode } from 'react';
import type { EntityName, EntitySecurity } from '../../../auth/Security';
import { EntitySecurityProvider, SECURITY_ACTIONS, useSecurity } from '../../../auth/Security';
import I18n, { useI18n } from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import { ShareSettingsProvider } from '../../../share';
import { PanelNoAccess } from '../../ui/components/PanelNoAccess';
import { usePanelCommands } from '../../ui/hooks/usePanelCommands';
import { usePanelSession } from '../../ui/hooks/usePanelSession';
import { useSharePanelData } from './data/useSharePanelData';
import type { SharePanelOptions } from './types';
import { SharePanelSettings } from './ui/SharePanelSettings';

const PANEL_SX = { p: 2, overflowY: 'auto', maxHeight: '100%' };
const HEADER_SX = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 };

function SharePanelStatus({ pending = false, message }: { pending?: boolean; message?: ReactNode }) {
    const { close } = usePanelCommands();
    const closeLabel = useI18n('dialog.close');
    const handleClose = useCallback(() => {
        close();
    }, [close]);
    return (
        <Box sx={PANEL_SX}>
            <Box sx={HEADER_SX}>
                <I18n token="share" />
                <Tooltip title={closeLabel}>
                    <IconButton aria-label={closeLabel} data-testid="share-panel-close" onClick={handleClose}>
                        <Icons.Close />
                    </IconButton>
                </Tooltip>
            </Box>
            {pending ? (
                <Skeleton data-testid="share-panel-pending" height={160} />
            ) : message ? (
                <Alert severity="error">{message}</Alert>
            ) : (
                <PanelNoAccess />
            )}
        </Box>
    );
}

function LoadedSharePanel({
    entityName,
    entityId,
    entityLabel,
    deepLinkUIStatePath,
    scopePrefix
}: SharePanelOptions & { entityName: EntityName; entityId: string }) {
    const { shareData, isLoadingShare, errorLoadingShare, isSupported, saveClient } = useSharePanelData(
        entityName,
        entityId
    );
    const { close } = usePanelCommands();
    const closeLabel = useI18n('dialog.close');
    const handleClose = useCallback(() => {
        close();
    }, [close]);
    const recordSecurity: EntitySecurity = shareData?.security ?? {};
    const decision = useSecurity(SECURITY_ACTIONS.SHARE, { entityName, entitySecurity: recordSecurity });
    if (!isSupported) return <SharePanelStatus message={<I18n token="common.share.settings.update.failure" />} />;
    if (isLoadingShare || decision.pending) return <SharePanelStatus pending />;
    if (errorLoadingShare || !shareData)
        return <SharePanelStatus message={<I18n token="common.share.settings.update.failure" />} />;
    if (!decision.allowed) return <SharePanelStatus />;
    return (
        <EntitySecurityProvider entityName={entityName} entitySecurity={recordSecurity}>
            <ShareSettingsProvider throwOnError client={saveClient}>
                <Box sx={PANEL_SX}>
                    <Box sx={HEADER_SX}>
                        <Typography>
                            <I18n token="share.panel.title" values={{ entityName: entityLabel ?? '' }} />
                        </Typography>
                        <Tooltip title={closeLabel}>
                            <IconButton aria-label={closeLabel} data-testid="share-panel-close" onClick={handleClose}>
                                <Icons.Close />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <SharePanelSettings
                        entity={shareData}
                        deepLinkUIStatePath={deepLinkUIStatePath}
                        scopePrefix={scopePrefix}
                    />
                </Box>
            </ShareSettingsProvider>
        </EntitySecurityProvider>
    );
}

export function ShareSidePanel({ entityLabel, deepLinkUIStatePath, scopePrefix }: SharePanelOptions) {
    const { subject, sessionId } = usePanelSession();
    const decision = useSecurity(SECURITY_ACTIONS.SHARE, {
        entityName: subject?.entityName,
        entitySecurity: subject?.security
    });
    if (decision.pending) return <SharePanelStatus pending />;
    if (!subject?.reference.id || !decision.allowed) return <SharePanelStatus />;
    return (
        <LoadedSharePanel
            key={`${sessionId}:${subject.entityName}:${subject.reference.id}`}
            entityName={subject.entityName}
            entityId={subject.reference.id}
            entityLabel={entityLabel ?? subject.label ?? subject.reference.label}
            deepLinkUIStatePath={deepLinkUIStatePath}
            scopePrefix={scopePrefix}
        />
    );
}
